import type { Transaction } from "./types";

/** LBS-style closing fee: total for each leg ≈ 0,64 % of Bausparsumme (Z5 R sample: 320 € on 50.000 €). */
const CLOSING_FEE_RATE_PCT = 0.64;

type ClosingFeeCategory = "bsv" | "avbv";

const CLOSING_FEE_CATEGORIES: {
  category: ClosingFeeCategory;
  pattern: RegExp;
  label: string;
}[] = [
  {
    category: "bsv",
    pattern: /(?:teil)?gebühr\s+bsv\s*-\s*abschluss/i,
    label: "BSV-Abschlussgebühr",
  },
  {
    category: "avbv",
    pattern: /(?:teil)?gebühr\s+avbv\s*-\s*abschluss/i,
    label: "AVBV-Abschlussgebühr",
  },
];

export interface BausparsummeInference {
  suggested_bausparsumme: number | null;
  bausparsumme_hint: string | null;
}

function categorizeClosingFee(text: string): ClosingFeeCategory | null {
  for (const { category, pattern } of CLOSING_FEE_CATEGORIES) {
    if (pattern.test(text)) return category;
  }
  return null;
}

function inferFromFeeTotal(amount: number): number {
  return Math.round(amount / (CLOSING_FEE_RATE_PCT / 100));
}

function formatEur(amount: number): string {
  const [intPart, dec] = amount.toFixed(2).split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${grouped},${dec}`;
}

export function inferBausparsummeFromTransactions(
  transactions: Transaction[],
): BausparsummeInference {
  const byCategory: Record<
    ClosingFeeCategory,
    { total: number; count: number; hasPartialLabel: boolean }
  > = {
    bsv: { total: 0, count: 0, hasPartialLabel: false },
    avbv: { total: 0, count: 0, hasPartialLabel: false },
  };

  for (const tx of transactions) {
    if (tx.amount >= 0) continue;
    const category = categorizeClosingFee(tx.text);
    if (!category) continue;

    const bucket = byCategory[category];
    bucket.total += Math.abs(tx.amount);
    bucket.count += 1;
    if (/teilgebühr/i.test(tx.text)) {
      bucket.hasPartialLabel = true;
    }
  }

  const legs: {
    category: ClosingFeeCategory;
    rounded: number;
    total: number;
    count: number;
    hasPartialLabel: boolean;
    label: string;
  }[] = [];

  for (const { category, label } of CLOSING_FEE_CATEGORIES) {
    const bucket = byCategory[category];
    if (bucket.count === 0) continue;
    legs.push({
      category,
      label,
      total: bucket.total,
      count: bucket.count,
      hasPartialLabel: bucket.hasPartialLabel,
      rounded: inferFromFeeTotal(bucket.total),
    });
  }

  if (legs.length === 0) {
    return { suggested_bausparsumme: null, bausparsumme_hint: null };
  }

  // Prefer BSV; each leg is summed across all its bookings (including partials on different dates).
  const primary = legs.find((l) => l.category === "bsv") ?? legs[0];

  const bookingWord = primary.count === 1 ? "booking" : "bookings";
  const partialNote =
    primary.hasPartialLabel && primary.count > 1
      ? " (summed partial fees)"
      : primary.hasPartialLabel
        ? " (Teilgebühr)"
        : "";

  return {
    suggested_bausparsumme: primary.rounded,
    bausparsumme_hint: `Inferred from ${primary.label}: ${primary.count} ${bookingWord}, total ${formatEur(primary.total)} € at 0,64 %${partialNote}.`,
  };
}
